import { useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';

import { safeLocalStorage, safeSessionStorage } from '../../utils/safeStorage';
import { TOTAL_REPORT_CARD_AREAS } from './reportCard.config';

const storageKey = (userId: string) => `report-card-dialog-opened:${userId}`;
const windowStartKey = (userId: string) => `report-card-pulse-window:${userId}`;

// WCAG 2.2.2: motion used to draw attention must not run indefinitely. The
// nudge gets one short window and then stops, whether or not it was noticed.
export const PULSE_WINDOW_MS = 5_000;

export interface ReportCardPulse {
	/** Run the attention pulse on the sidebar card. */
	shouldPulse: boolean;
	/** Retires the pulse for this user for good — call when the dialog opens. */
	onDialogOpened: () => void;
}

/**
 * Attention-pulse decision for the sidebar report card: the card pulses once
 * the user has earned their first grade but hasn't yet looked at the Zeugnis,
 * to lift the card's click-through.
 *
 * Armed on the first completed area and retired permanently the first time the
 * dialog is opened (persisted per user). Suppressed at the perfect grade —
 * there is no area left to activate on, and it would otherwise compete with
 * the celebratory check draw in `GradeCheckReveal`.
 *
 * The window is `PULSE_WINDOW_MS` per **tab session**, not per mount: each page
 * renders its own `MasterLayout`, so this card is re-created on every in-app
 * navigation and mount-local state would hand out a fresh window each time. The
 * arm time is persisted in `sessionStorage` instead, so a navigation resumes
 * what is left of the window and a further completed area cannot restart it. A
 * visit where nobody was looking doesn't spend the nudge for good — a new tab
 * gets a new window.
 *
 * `enabled` must be the card's eligibility — the experiment gate resolves
 * async, and arming while the card still renders null would spend the window
 * off-screen.
 */
export function useReportCardPulse(
	completedCount: number,
	userId?: string,
	enabled = true
): ReportCardPulse {
	const reducedMotion = useReducedMotion();
	const [openedThisMount, setOpenedThisMount] = useState(false);
	// The window running out is not a React event; the timer below nudges a
	// re-render at that moment. `deadline` is the gate, this state is not read.
	const [, closeWindow] = useState(false);
	// Read lazily rather than in a state initializer: the user arrives async, so
	// the first render has no id to key the lookup on.
	const openedBeforeRef = useRef<boolean | 'unread'>('unread');
	const deadlineRef = useRef<number | null>(null);

	if (openedBeforeRef.current === 'unread' && userId) {
		openedBeforeRef.current = safeLocalStorage.getItem(storageKey(userId)) !== null;
	}

	const retired = openedBeforeRef.current === true || openedThisMount;
	const graded = completedCount > 0 && completedCount < TOTAL_REPORT_CARD_AREAS;
	const eligibleToPulse = enabled && !!userId && !reducedMotion && !retired && graded;

	// Resolved the first time the pulse is otherwise eligible in this mount,
	// continuing a window an earlier mount already opened in this tab session.
	if (eligibleToPulse && userId && deadlineRef.current === null) {
		const storedStart = Number(safeSessionStorage.getItem(windowStartKey(userId)));
		const startedAt = Number.isFinite(storedStart) && storedStart > 0 ? storedStart : Date.now();
		deadlineRef.current = startedAt + PULSE_WINDOW_MS;
	}

	const deadline = deadlineRef.current;
	const windowClosed = deadline === null || Date.now() >= deadline;

	useEffect(() => {
		if (deadline === null || !userId) {
			return undefined;
		}
		// Written on every mount rather than only when absent, so a garbled value
		// heals instead of handing out a fresh window per navigation forever.
		safeSessionStorage.setItem(windowStartKey(userId), String(deadline - PULSE_WINDOW_MS));
		const remaining = deadline - Date.now();
		if (remaining <= 0) {
			return undefined;
		}
		const timer = setTimeout(() => closeWindow(true), remaining);
		return () => clearTimeout(timer);
	}, [deadline, userId]);

	const onDialogOpened = useCallback(() => {
		if (!userId) {
			return;
		}
		safeLocalStorage.setItem(storageKey(userId), '1');
		setOpenedThisMount(true);
	}, [userId]);

	return { shouldPulse: eligibleToPulse && !windowClosed, onDialogOpened };
}
